import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import AuthService from '@/services/AuthService';
import {
  AdminAccount,
  CacheEntry,
  CacheSetting,
  CacheStats,
  CatalogContentRow,
  DTO,
  Feedback,
  Label,
  List,
  ListSummary,
  Project,
  TicketWithEvents,
  User,
  Work,
  Workorder,
  WorkorderLabor,
  WorkorderMaterial,
} from '@certhon/models';
import { UseMutationOptions } from '@tanstack/react-query';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MutOpt<F extends (...args: any[]) => Promise<any>> =
  UseMutationOptions<Awaited<ReturnType<F>>, Error, Parameters<F>[0]>;

// TODO: move PlanGroup to @certhon/models
/** Planning group with associated user IDs */
export interface Plangroup {
  id: number;
  name: string;
  user_ids: number[];
}

export type resources = {
  '/projects': DTO<Project>[];
  '/projects/:recnum': DTO<Project>; // use this by casting
  '/tickets': DTO<TicketWithEvents>[];
  '/ticketevents': DTO<TicketWithEvents>[];
  '/lists': DTO<ListSummary>[];
  '/listevents': DTO<List>[];
  '/users': DTO<User>[];
  '/labels': DTO<Label>[];
  '/works': DTO<Work>[];
  '/works/:recnum': DTO<Work>; // use this by casting
  '/workorders': DTO<Workorder>[];
  '/workorder_labor': DTO<WorkorderLabor>[];
  '/workorder_materials': DTO<WorkorderMaterial>[];
  '/admin/accounts': DTO<AdminAccount>[];
  '/admin/accounts/:recnum': DTO<AdminAccount>;
  '/feedback': DTO<Feedback>[];
  '/attachments/:checksum': unknown; // use this by casting
  '/plangroups': Plangroup[];
  '/catalog_content': DTO<CatalogContentRow>[];
  '/cache_entries': CacheEntry[];
  '/cache_stats': CacheStats[];
  '/cache_settings': CacheSetting[];
};

export function fetchApi<R extends keyof resources>(
  resource: R,
  qry?: Record<string, string | number>,
  init?: RequestInit,
): Promise<resources[R]> {
  return AuthService.getInstance()
    .waitForReady() // allow the authservice to refresh accesstoken
    .then(() =>
      fetch(resolveUrl(resource, qry), {
        ...init,
        headers: {
          'content-type': 'application/json',
          prefer: 'return=representation',
          // "x-client-version": process.env.REACT_APP_VERSION || undefined,
          // authorization: `Bearer ${token}`,
          ...init?.headers,
        },
        credentials: 'include',
      })
        .then(verifyResponse)
        .then((res) => res.json()),
    );
}

/**
 * PostgREST RPC: POST /rpc/<fn> with named args. Void functions yield no
 * body; scalar ones the bare scalar.
 */
export async function rpcApi(
  fn: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const res = await fetch(resolveUrl(`/rpc/${fn}` as keyof resources), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  }).then(verifyResponse);
  const text = await res.text();
  return text ? JSON.parse(text) : undefined;
}
